import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { SystemControlValueQueryParameters } from '@quorum/models/xs-query';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import * as fromSystemControl from './+state/system-control.actions';
import { selectEntities } from './+state/system-control.interfaces';
import { SystemControlValue } from '@quorum/models/xs-resource';

interface PendingRequest {
  missingRefs: string[];
  rooftopId: number;
  observable: Observable<any>;
  subjects: Subject<Record<string, SystemControlValue>>[];
}
@Injectable()
export class SystemControlStateService {
  constructor(private store: Store<any>) {}

  getSystemControlValue(constantRef: string, rooftopId: number) {
    this.store
      .pipe(
        select(selectEntities),
        take(1),
        map((systemControlValues) => {
          let systemControlExists = Object.keys(systemControlValues).includes(`${constantRef}-${rooftopId}`);
          let params = new SystemControlValueQueryParameters({
            constantReference: constantRef,
            rooftopId: rooftopId.toString(),
          });

          if (!systemControlExists) {
            this.store.dispatch(new fromSystemControl.GetSystemControlValuesFromServer(params));
          }
        })
      )
      .subscribe();

    return this.store.pipe(
      select(selectEntities),
      map((systemControlValues) => {
        let filtered = Object.keys(systemControlValues)
          .filter((key) => constantRef === key.substr(0, key.indexOf('-')))
          .reduce((obj, key) => {
            return systemControlValues[key];
          }, {});
        return filtered;
      }),
      filter((systemControlValues: any) => {
        return !this.isEmpty(systemControlValues);
      })
    );
  }

  // OG function, leaving here for reference and in case of rollback
  // This function was replaced with a more efficient version that handles multiple flags and deduplication

  // getSystemControlValues(constantRefs: string[], rooftopId: number) {
  //   this.store
  //     .pipe(
  //       select(selectEntities),
  //       take(1),
  //       map((systemControlValues) => {
  //         //console.log('getSystemControlValues', constantRefs, rooftopId, systemControlValues);
  //         let constantRefsArray = constantRefs.filter(
  //           (x) => !Object.keys(systemControlValues).includes(x + '-' + rooftopId)
  //         );
  //         let params = new SystemControlValueQueryParameters({
  //           constantReference: constantRefsArray.join(','),
  //           rooftopId: rooftopId.toString(),
  //         });
  //         //console.log('constantRefsArray', constantRefsArray, params);
  //         // If there are any constant references that are not yet loaded, dispatch an action to fetch

  //         if (constantRefsArray.length > 0) {
  //           this.store.dispatch(new fromSystemControl.GetSystemControlValuesFromServer(params));
  //         }
  //       })
  //     )
  //     .subscribe();

  //   return this.store.pipe(
  //     select(selectEntities),
  //     map((systemControlValues) => {
  //       let filtered = Object.keys(systemControlValues)
  //         .filter((key) => constantRefs.includes(key.substr(0, key.indexOf('-'))))
  //         .reduce((obj, key) => {
  //           return {
  //             ...obj,
  //             [key.substr(0, key.indexOf('-'))]: systemControlValues[key],
  //           };
  //         }, {});
  //       //console.log('getSystemControlValues filtered', filtered);
  //       return filtered;
  //     }),
  //     filter((systemControlValues: any) => {
  //       return !this.isEmpty(systemControlValues) && Object.keys(systemControlValues).length === constantRefs.length;
  //     })
  //   );
  // }

  private pendingRequests = new Map<number, PendingRequest>();

  // Maps rooftopId to set of flag names currently being fetched
  private pendingFlags = new Map<string, Set<string>>();

  /**
   * Gets system control values for specified flags and rooftop.
   * Prevents duplicate API calls by tracking individual flags being requested.
   * Returns values from state if available, otherwise fetches missing flags from server.
   */
  getSystemControlValues(constantRefs: string[], rooftopId: number): Observable<Record<string, SystemControlValue>> {
    return this.store.pipe(
      select(selectEntities),
      take(1),
      switchMap((systemControlValues) => {
        // Check which flags are missing from NgRx state
        const missingFromState = constantRefs.filter(
          (ref) => !Object.keys(systemControlValues).includes(`${ref}-${rooftopId}`)
        );

        if (missingFromState.length === 0) {
          // All requested flags are already in state - return immediately
          return of(this.filterValues(systemControlValues, constantRefs, rooftopId));
        }

        // Check which flags are currently being fetched from server
        const rooftopKey = rooftopId.toString();
        const currentlyPending = this.pendingFlags.get(rooftopKey) || new Set<string>();

        // Find flags that need to be fetched (missing from state AND not already being requested)
        const trulyMissing = missingFromState.filter((ref) => !currentlyPending.has(ref));

        // Check if there's already a pending request for this rooftop
        const existingRequest = this.pendingRequests.get(rooftopId);

        if (trulyMissing.length > 0) {
          // Some flags need to be fetched from server
          if (existingRequest) {
            // Add new flags to existing server request
            return this.addToExistingRequest(existingRequest, trulyMissing, constantRefs, rooftopId);
          } else {
            // Create brand new server request
            return this.createNewRequest(trulyMissing, constantRefs, rooftopId);
          }
        } else {
          // All missing flags are already being requested from server
          if (existingRequest) {
            // Wait for existing server request to complete
            return this.waitForExistingRequest(existingRequest, constantRefs, rooftopId);
          } else {
            // Edge case: wait for values to appear in state
            return this.waitForValuesInState(constantRefs, rooftopId);
          }
        }
      })
    );
  }

  /**
   * Creates a new server request for missing flags.
   * Dispatches NgRx action to fetch data and sets up tracking for deduplication.
   */
  private createNewRequest(missingRefs: string[], requestedRefs: string[], rooftopId: number): Observable<any> {
    // Track which flags are now being requested to prevent duplicate calls
    const rooftopKey = rooftopId.toString();
    const pendingSet = this.pendingFlags.get(rooftopKey) || new Set<string>();
    missingRefs.forEach((ref) => pendingSet.add(ref));
    this.pendingFlags.set(rooftopKey, pendingSet);

    // Dispatch NgRx action to fetch missing flags from server
    const params = new SystemControlValueQueryParameters({
      constantReference: missingRefs.join(','),
      rooftopId: rooftopId.toString(),
    });
    this.store.dispatch(new fromSystemControl.GetSystemControlValuesFromServer(params));

    // Create observable that will emit when server response is received
    const requestSubject = new Subject<Record<string, SystemControlValue>>();

    // Store request info for other calls to reference
    const pendingRequest: PendingRequest = {
      missingRefs,
      rooftopId,
      observable: this.waitForValuesInState(missingRefs, rooftopId).pipe(
        tap(() => {
          // Clean up tracking when server request completes
          this.cleanupRequest(rooftopId, missingRefs);
        }),
        shareReplay(1)
      ),
      subjects: [requestSubject],
    };

    this.pendingRequests.set(rooftopId, pendingRequest);

    // When server data arrives, emit the specific values this caller requested
    pendingRequest.observable.subscribe({
      next: () => {
        this.emitRequestedValues(requestedRefs, rooftopId, requestSubject);
      },
      error: (error) => requestSubject.error(error),
    });

    return requestSubject.asObservable();
  }

  /**
   * Adds additional flags to an existing server request.
   * Used when some flags are already being fetched but we need additional ones.
   */
  private addToExistingRequest(
    existingRequest: PendingRequest,
    additionalRefs: string[],
    requestedRefs: string[],
    rooftopId: number
  ): Observable<any> {
    // Track the additional flags being requested
    const rooftopKey = rooftopId.toString();
    const pendingSet = this.pendingFlags.get(rooftopKey) || new Set<string>();
    additionalRefs.forEach((ref) => pendingSet.add(ref));
    this.pendingFlags.set(rooftopKey, pendingSet);

    // Dispatch separate NgRx action for the additional flags
    const params = new SystemControlValueQueryParameters({
      constantReference: additionalRefs.join(','),
      rooftopId: rooftopId.toString(),
    });
    this.store.dispatch(new fromSystemControl.GetSystemControlValuesFromServer(params));

    // Update existing request to track all flags being fetched
    existingRequest.missingRefs = [...existingRequest.missingRefs, ...additionalRefs];

    // Create new subject for this specific caller
    const requestSubject = new Subject<Record<string, SystemControlValue>>();
    existingRequest.subjects.push(requestSubject);

    // Wait for all requested values to be available in state, then emit to caller
    this.waitForValuesInState(requestedRefs, rooftopId).subscribe({
      next: (values) => {
        requestSubject.next(values);
      },
      error: (error) => requestSubject.error(error),
      complete: () => requestSubject.complete(),
    });

    return requestSubject.asObservable();
  }

  /**
   * Waits for an existing server request to complete without making additional API calls.
   * Used when all requested flags are already being fetched by another call.
   */
  private waitForExistingRequest(
    existingRequest: PendingRequest,
    requestedRefs: string[],
    rooftopId: number
  ): Observable<any> {
    // Create new subject for this caller to receive results
    const requestSubject = new Subject<Record<string, SystemControlValue>>();
    existingRequest.subjects.push(requestSubject);

    // Wait for the requested flags to become available in state
    this.waitForValuesInState(requestedRefs, rooftopId).subscribe({
      next: (values) => requestSubject.next(values),
      error: (error) => requestSubject.error(error),
      complete: () => requestSubject.complete(),
    });

    return requestSubject.asObservable();
  }

  /**
   * Retrieves current values from NgRx state and emits them to the caller's subject.
   * Used to provide the final response once server data has been loaded into state.
   */
  private emitRequestedValues(
    constantRefs: string[],
    rooftopId: number,
    subject: Subject<Record<string, SystemControlValue>>
  ): void {
    this.store
      .pipe(
        select(selectEntities),
        map((systemControlValues) => this.filterValues(systemControlValues, constantRefs, rooftopId)),
        take(1)
      )
      .subscribe({
        next: (values) => {
          subject.next(values);
          subject.complete();
        },
        error: (error) => subject.error(error),
      });
  }

  /**
   * Waits for specified flags to become available in NgRx state.
   * Continuously monitors state until all requested flags are present.
   */
  private waitForValuesInState(constantRefs: string[], rooftopId: number): Observable<any> {
    return this.store.pipe(
      select(selectEntities),
      map((systemControlValues) => this.filterValues(systemControlValues, constantRefs, rooftopId)),
      filter((filtered: any) => {
        // Only emit when we have all requested flags
        return !this.isEmpty(filtered) && Object.keys(filtered).length === constantRefs.length;
      }),
      take(1)
    );
  }

  /**
   * Cleans up tracking data when a server request completes.
   * Removes completed flags from pending sets and clears request cache.
   */
  private cleanupRequest(rooftopId: number, completedRefs: string[]): void {
    // Remove completed flags from the pending flags set
    const rooftopKey = rooftopId.toString();
    const pendingSet = this.pendingFlags.get(rooftopKey);
    if (pendingSet) {
      completedRefs.forEach((ref) => pendingSet.delete(ref));
      // Remove the entire set if no flags are pending for this rooftop
      if (pendingSet.size === 0) {
        this.pendingFlags.delete(rooftopKey);
      }
    }

    // Remove the completed request from cache
    this.pendingRequests.delete(rooftopId);
  }

  /**
   * Filters NgRx state to return only the requested flags for the specified rooftop.
   * Transforms state keys (flag-rooftopId) back to simple flag names for the response.
   */
  private filterValues(systemControlValues: any, constantRefs: string[], rooftopId: number): any {
    return Object.keys(systemControlValues)
      .filter((key) => {
        // Parse the state key format: "flagName-rooftopId"
        const ref = key.substring(0, key.indexOf('-'));
        const keyRooftopId = key.substring(key.indexOf('-') + 1);
        return constantRefs.includes(ref) && keyRooftopId === rooftopId.toString();
      })
      .reduce((obj, key) => {
        // Extract flag name from state key and add to result object
        const ref = key.substring(0, key.indexOf('-'));
        return {
          ...obj,
          [ref]: systemControlValues[key],
        };
      }, {});
  }

  /**
   * Checks if an object is empty (has no properties).
   */
  private isEmpty(obj: object | null | undefined): boolean {
    return obj && Object.keys(obj).length === 0;
  }

  /**
   * Clears all pending request tracking and completes any active subjects.
   * Useful for testing, cleanup, or resetting service state.
   */
  clearPendingRequests(): void {
    // Complete all active subjects to prevent memory leaks
    this.pendingRequests.forEach((request) => {
      request.subjects.forEach((subject) => {
        if (!subject.closed) {
          subject.complete();
        }
      });
    });
    // Clear all tracking maps
    this.pendingRequests.clear();
    this.pendingFlags.clear();
  }
}
